import React, { useState } from "react";
import { Home, Search, Clock, User, QrCode, Ticket, Info, LayoutDashboard, LogOut, MapPin, X, ChevronRight, Settings, Film } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useUser, useClerk, SignedIn, SignedOut } from "@clerk/clerk-react";
import { motion, AnimatePresence } from "framer-motion";
import { SwipeableDrawer, Box, Typography, Avatar, IconButton, Divider, List, ListItem, ListItemIcon, ListItemText, ListItemButton } from "@mui/material";
import "./MobileBottomNav.css";

const MobileBottomNav = ({ onOpenSearch, onOpenLocation }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isSignedIn } = useUser();
  const { signOut, openUserProfile } = useClerk();
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const isActive = (path) => location.pathname === path;

  React.useEffect(() => {
    const handleOpenProfile = () => setShowProfileMenu(true);
    window.addEventListener('open-mobile-profile', handleOpenProfile);
    return () => window.removeEventListener('open-mobile-profile', handleOpenProfile);
  }, []);

  const menuItems = [
    { label: "My Bookings", icon: <Ticket size={20} />, path: "/my-bookings" },
    { label: "About Us", icon: <Info size={20} />, path: "/about" },
    ...(user?.publicMetadata?.role === "admin" ? [{ label: "Admin Dashboard", icon: <LayoutDashboard size={20} />, path: "/admin" }] : []),
  ];

  const handleProfileClick = () => {
    if (!isSignedIn) {
      navigate("/sign-in");
    } else {
      setShowProfileMenu(true);
    }
  };

  const handleManageAccount = () => {
    setShowProfileMenu(false);
    openUserProfile();
  };

  return (
    <>
      <div className="mobile-bottom-nav-container">
        <div className="mobile-bottom-nav-glass">
          <Link to="/" className={`nav-item ${isActive("/") ? "active" : ""}`}>
            <motion.div whileTap={{ scale: 0.9 }} className="nav-icon-wrapper">
              <Home size={22} strokeWidth={isActive("/") ? 2.5 : 2} />
            </motion.div>
            <span className="nav-label">Home</span>
            {isActive("/") && <motion.div layoutId="nav-glow" className="nav-glow" />}
          </Link>

          <Link to="/movies" className={`nav-item ${isActive("/movies") ? "active" : ""}`}>
            <motion.div whileTap={{ scale: 0.9 }} className="nav-icon-wrapper">
              <Film size={22} strokeWidth={isActive("/movies") ? 2.5 : 2} />
            </motion.div>
            <span className="nav-label">Movies</span>
            {isActive("/movies") && <motion.div layoutId="nav-glow" className="nav-glow" />}
          </Link>

          <button className="nav-item search-center-btn" onClick={onOpenSearch}>
            <motion.div
              whileTap={{ scale: 0.8 }}
              className="nav-icon-wrapper search-fab"
            >
              <Search size={24} color="#fff" strokeWidth={2.5} />
            </motion.div>
          </button>

          <Link to="/my-bookings" className={`nav-item ${isActive("/my-bookings") ? "active" : ""}`}>
            <motion.div whileTap={{ scale: 0.9 }} className="nav-icon-wrapper">
              <Clock size={22} strokeWidth={isActive("/my-bookings") ? 2.5 : 2} />
            </motion.div>
            <span className="nav-label">History</span>
            {isActive("/my-bookings") && <motion.div layoutId="nav-glow" className="nav-glow" />}
          </Link>

          <button className={`nav-item ${showProfileMenu ? "active" : ""}`} onClick={handleProfileClick}>
            <motion.div whileTap={{ scale: 0.9 }} className="nav-icon-wrapper">
              {isSignedIn && user?.imageUrl ? (
                <img
                  src={user.imageUrl}
                  alt="Profile"
                  style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', border: showProfileMenu ? '2px solid #6366f1' : 'none' }}
                />
              ) : (
                <User size={22} strokeWidth={showProfileMenu ? 2.5 : 2} />
              )}
            </motion.div>
            <span className="nav-label">Profile</span>
          </button>
        </div>
      </div>

      <SwipeableDrawer
        anchor="bottom"
        open={showProfileMenu}
        onClose={() => setShowProfileMenu(false)}
        onOpen={() => setShowProfileMenu(true)}
        disableBackdropTransition={false}
        disableDiscovery={false}
        swipeAreaWidth={56}
        PaperProps={{
          style: {
            borderTopLeftRadius: 32,
            borderTopRightRadius: 32,
            maxWidth: 500,
            margin: "0 auto",
            backgroundColor: "white",
            overflow: "hidden"
          }
        }}
      >
        <Box sx={{ width: 'auto', p: 3, pb: 5 }}>
          <Box className="sheet-handle" sx={{ mb: 3 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={user?.imageUrl}
                sx={{ width: 64, height: 64, borderRadius: 2.5, border: '2px solid #6366f1' }}
              />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>
                  {user?.fullName || "Guest User"}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  {user?.primaryEmailAddress?.emailAddress || user?.username}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={() => setShowProfileMenu(false)} sx={{ backgroundColor: '#f1f5f9' }}>
              <X size={20} />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <ListItemButton
              onClick={handleManageAccount}
              sx={{ borderRadius: 4, p: 2, gap: 2 }}
            >
              <Box className="item-icon-wrap" sx={{ backgroundColor: '#f1f5f9', color: '#6366f1' }}>
                <Settings size={22} />
              </Box>
              <ListItemText
                primary={<Typography sx={{ fontWeight: 600 }}>Manage Account</Typography>}
                secondary="Profile, security & more"
              />
              <ChevronRight size={18} color="#cbd5e1" />
            </ListItemButton>

            <ListItemButton
              onClick={() => { setShowProfileMenu(false); onOpenLocation(); }}
              sx={{ borderRadius: 4, p: 2, gap: 2 }}
            >
              <Box className="item-icon-wrap" sx={{ backgroundColor: '#f1f5f9', color: '#6366f1' }}>
                <MapPin size={22} />
              </Box>
              <ListItemText
                primary={<Typography sx={{ fontWeight: 600 }}>Change Location</Typography>}
                secondary="Select your cinema city"
              />
              <ChevronRight size={18} color="#cbd5e1" />
            </ListItemButton>

            <ListItemButton
              onClick={() => { navigate("/partner"); setShowProfileMenu(false); }}
              sx={{ borderRadius: 4, p: 2, gap: 2 }}
            >
              <Box className="item-icon-wrap" sx={{ backgroundColor: '#f1f5f9', color: '#8b5cf6' }}>
                <LayoutDashboard size={22} />
              </Box>
              <ListItemText
                primary={<Typography sx={{ fontWeight: 600 }}>Partner Hub</Typography>}
                secondary="Manage your theaters"
              />
              <ChevronRight size={18} color="#cbd5e1" />
            </ListItemButton>

            <Divider sx={{ my: 1 }} />

            {menuItems.map((item) => (
              <ListItemButton
                key={item.path}
                onClick={() => { navigate(item.path); setShowProfileMenu(false); }}
                sx={{ borderRadius: 4, p: 2, gap: 2 }}
              >
                <Box className="item-icon-wrap" sx={{ backgroundColor: '#f1f5f9', color: '#6366f1' }}>
                  {item.icon}
                </Box>
                <ListItemText
                  primary={<Typography sx={{ fontWeight: 600 }}>{item.label}</Typography>}
                />
                <ChevronRight size={18} color="#cbd5e1" />
              </ListItemButton>
            ))}

            <Divider sx={{ my: 1 }} />

            <ListItemButton
              onClick={() => { signOut(); setShowProfileMenu(false); }}
              sx={{ borderRadius: 4, p: 2, gap: 2, color: '#ef4444' }}
            >
              <Box className="item-icon-wrap" sx={{ backgroundColor: '#fef2f2', color: '#ef4444' }}>
                <LogOut size={22} />
              </Box>
              <ListItemText
                primary={<Typography sx={{ fontWeight: 600 }}>Sign Out</Typography>}
              />
            </ListItemButton>
          </Box>
        </Box>
      </SwipeableDrawer>
    </>
  );
};

export default MobileBottomNav;
